import { formatDateTimeMX } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useEffect, useState } from 'react'
import { X, User, Calendar, Mail, Phone, Briefcase, Download, Eye, XCircle, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { fileService } from '@/services/fileService'
import { apiService } from '@/services/api'
import type { Candidate } from '@/services/api'
import { experienciaDelCandidato } from "@/features/rh/lib/experienciaDelCandidato";
import { Badge } from '@/shared/components/Badge'
import { colorDeEstatusDeCandidato } from '@/features/rh/lib/colorDeEstatusDeCandidato'
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";

interface CandidatesListProps {
  jobRequestId: number
  jobTitle: string
  isOpen: boolean
  onClose: () => void
}

const CandidatesList = ({ jobRequestId, jobTitle, isOpen, onClose }: CandidatesListProps) => {
  const navigate = useNavigate()
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && jobRequestId) {
      loadCandidates()
    }
  }, [isOpen, jobRequestId])

  // Bloquear scroll cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isOpen])

  const loadCandidates = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await apiService.getCandidatesByJobRequest(jobRequestId)
      setCandidates(data || [])
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar candidatos:', err)
      setError(err.message || 'Error al cargar los candidatos')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status: Candidate['status']) => {
    const etiquetas: Record<string, string> = {
      pre_registro: 'Pre-registro',
      registrado: 'Registrado',
      pendiente: 'Pendiente',
      en_proceso: 'En Proceso',
      entrevista_rh: 'Entrevista RH',
      entrevista_solicitante: 'Aprobado por RH',
      pre_seleccionado: 'Aprobado por RH',
      // RH-03.1: siguen aprobados por RH, pero cada uno en su momento.
      pendiente_cierre: 'Por cerrar (RH)',
      pendiente_direccion: 'En validación de Dirección',
      decision_direccion: 'Decisión de Dirección recibida',
      seleccionado: 'Seleccionado',
      rechazado_evaluacion_rh: 'Rechazado RH',
      contratado: 'Contratado',
      rechazado: 'Rechazado',
    }

    const clave = etiquetas[status] ? status : 'registrado'

    return (
      <Badge color={colorDeEstatusDeCandidato(clave, 'azul')}>{etiquetas[clave]}</Badge>
    )
  }

  const handleDownloadCV = async (cvUrl: string, candidateName: string) => {
    try {
      const fileName = `CV_${candidateName.replace(/\s+/g, '_')}.pdf`;
      await fileService.downloadFile(cvUrl, fileName);
    } catch (error) {
      console.error('Error al descargar CV:', error);
      // Podrías mostrar un mensaje de error al usuario aquí
    }
  }

  const handleViewCandidateDetail = (candidateId: string) => {
    navigate(`/candidate/${candidateId}`)
  }

  if (!isOpen) return null

  return (
    <CapaDeModal zIndex={10000} onClickFondo={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Candidatos Registrados
            </h2>
            <p className="text-gray-600 mt-1">
              {jobTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600">Cargando candidatos...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && candidates.length === 0 && (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No hay candidatos registrados
              </h3>
              <p className="text-gray-600">
                Aún no se han registrado candidatos para esta vacante.
              </p>
            </div>
          )}

          {/* Candidates List */}
          {!isLoading && !error && candidates.length > 0 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-blue-900">
                      {candidates.length} candidato{candidates.length !== 1 ? 's' : ''} registrado{candidates.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex space-x-4 text-sm">
                    <span className="text-blue-700">
                      Nuevos: {candidates.filter(c => c.status === 'registrado').length}
                    </span>
                    <span className="text-yellow-700">
                      En proceso: {candidates.filter(c => c.status === 'en_proceso').length}
                    </span>
                    <span className="text-green-700">
                      Contratados: {candidates.filter(c => c.status === 'contratado').length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Candidates Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {candidates.map((candidate) => (
                  <div
                    key={candidate.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {candidate.fullName}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Calendar className="w-4 h-4 mr-1" />
                          {candidate.age} años
                        </div>
                      </div>
                      {getStatusBadge(candidate.status)}
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-blue-500" />
                        <a 
                          href={`mailto:${candidate.email}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {candidate.email}
                        </a>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-green-500" />
                        <a 
                          href={`tel:${candidate.phone}`}
                          className="text-green-600 hover:text-green-800"
                        >
                          {candidate.phone}
                        </a>
                      </div>
                    </div>

                    {/* Experience */}
                    <div className="mb-4">
                      <div className="flex items-center text-sm text-gray-700 mb-2">
                        <Briefcase className="w-4 h-4 mr-2 text-purple-500" />
                        <span className="font-medium">
                          {experienciaDelCandidato(candidate.experienceYears)} de experiencia
                        </span>
                      </div>
                      {candidate.experienceDescription && (
                        <p className="text-sm text-gray-600 line-clamp-3">
                          {candidate.experienceDescription}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="mb-4 flex flex-wrap gap-2">
                      {candidate.cvUrl && (
                        <button
                          onClick={() => handleDownloadCV(candidate.cvUrl!, candidate.fullName)}
                          className={estiloDeBoton("secundario")}
                        >
                          <Download className="w-4 h-4" />
                          Descargar CV
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleViewCandidateDetail(candidate.id)}
                        className={estiloDeBoton("secundario")}
                      >
                        <Eye className="w-4 h-4" />
                        Ver Detalle
                      </button>
                    </div>

                    {/* Registration Date */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          Registrado: {formatDateTimeMX(candidate.createdAt)}
                        </span>
                        {candidate.emailConfirmed && (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Email confirmado
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className={estiloDeBoton("secundario")}
          >
            Cerrar
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}

export default CandidatesList
